import React from 'react';
import { Link } from 'react-router-dom';
import { 
  UserGroupIcon, 
  AcademicCapIcon, 
  WrenchScrewdriverIcon 
} from '@heroicons/react/24/outline';

const Team = () => {
  const teamSections = [
    {
      title: "პირველი გუნდი",
      description: "ჩვენი მთავარი გუნდის ფეხბურთელები და მათი სტატისტიკა",
      icon: UserGroupIcon,
      href: "/team/first-team",
      color: "bg-blue-500"
    },
    {
      title: "მეორე გუნდი",
      description: "ჩვენი მეორე გუნდის ფეხბურთელები და მათი სტატისტიკა",
      icon: UserGroupIcon,
      href: "/team/second-team",
      color: "bg-red-500"
    },
    {
      title: "სამწვრთნელო შტაბი",
      description: "მთავარი მწვრთნელი და მისი ასისტენტები",
      icon: AcademicCapIcon,
      href: "/team/coaching-staff",
      color: "bg-green-500"
    },
    {
      title: "ტექნიკური პერსონალი",
      description: "ტექნიკური დირექტორი, სპორტის ექიმი და სხვა სპეციალისტები",
      icon: WrenchScrewdriverIcon,
      href: "/team/technical-staff",
      color: "bg-purple-500"
    }
  ];

  const firstTeamPlayers = [
    { name: "გიორგი ლომია", position: "მეკარე", number: 1 },
    { name: "ალექსანდრე კახაბერი", position: "მცველი", number: 4 },
    { name: "ნიკა ქაცარავა", position: "მცველი", number: 5 },
    { name: "ლევან ბერია", position: "მცველი", number: 3 },
    { name: "გიორგი ჯიქია", position: "ნახევარმცველი", number: 8 },
    { name: "ვალერი ყაზაიშვილი", position: "ნახევარმცველი", number: 10 },
    { name: "გიორგი ჩაკვეტაძე", position: "თავდამსხმელი", number: 9 }
  ];

  const secondTeamPlayers = [
    { name: "დავით ქართველიშვილი", position: "მეკარე", number: 1 },
    { name: "თორნიკე ბერიძე", position: "მცველი", number: 2 },
    { name: "ლაშა ჯანაშვილი", position: "მცველი", number: 3 },
    { name: "გიორგი ხარაბაძე", position: "მცველი", number: 4 },
    { name: "ნიკოლოზ ლაშხია", position: "ნახევარმცველი", number: 6 },
    { name: "ალექსანდრე ჩიქოვანი", position: "ნახევარმცველი", number: 7 },
    { name: "ლევან ყიფიანი", position: "თავდამსხმელი", number: 11 }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 font-georgian mb-4">
            გუნდი
          </h1>
          <p className="text-xl text-gray-600 font-georgian max-w-3xl mx-auto">
            ჩვენი გუნდის შესახებ - ფეხბურთელები, მწვრთნელები და ტექნიკური პერსონალი
          </p>
        </div>

        {/* Team Sections Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {teamSections.map((section) => (
            <Link
              key={section.title}
              to={section.href}
              className="group bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow duration-300"
            >
              <div className="text-center">
                <div className={`${section.color} p-4 rounded-full w-16 h-16 mx-auto mb-4`}>
                  <section.icon className="h-8 w-8 text-white mx-auto" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 font-georgian mb-2 group-hover:text-primary-600 transition-colors">
                  {section.title}
                </h3>
                <p className="text-gray-600 font-georgian">
                  {section.description}
                </p>
              </div>
            </Link>
          ))}
        </div>

        {/* First Team Preview */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-6">
            პირველი გუნდი - ძირითადი შემადგენლობა
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {firstTeamPlayers.map((player, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {player.number}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 font-georgian">{player.name}</h4>
                    <p className="text-sm text-gray-600 font-georgian">{player.position}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link
              to="/team/first-team"
              className="inline-flex items-center bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 font-georgian"
            >
              სრული შემადგენლობის ნახვა
            </Link>
          </div>
        </div>

        {/* Second Team Preview */}
        <div className="mt-8 bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-6">
            მეორე გუნდი - ძირითადი შემადგენლობა
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {secondTeamPlayers.map((player, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {player.number}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 font-georgian">{player.name}</h4>
                    <p className="text-sm text-gray-600 font-georgian">{player.position}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link
              to="/team/second-team"
              className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 font-georgian"
            >
              სრული შემადგენლობის ნახვა
            </Link>
          </div>
        </div>

        {/* Coaching Staff Preview */}
        <div className="mt-8 bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-6">
            სამწვრთნელო შტაბი
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="w-20 h-20 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4">
                მ.მ.
              </div>
              <h4 className="font-semibold text-gray-900 font-georgian mb-2">მთავარი მწვრთნელი</h4>
              <p className="text-sm text-gray-600 font-georgian">ზურაბ ლომიძე</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4">
                ას.
              </div>
              <h4 className="font-semibold text-gray-900 font-georgian mb-2">ასისტენტი მწვრთნელი</h4>
              <p className="text-sm text-gray-600 font-georgian">ზაზა ლაცაბიძე</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4">
                მეკ.
              </div>
              <h4 className="font-semibold text-gray-900 font-georgian mb-2">მეკარეების მწვრთნელი</h4>
              <p className="text-sm text-gray-600 font-georgian">ბორის დერევიაჩენკო</p>
            </div>
          </div>
          <div className="mt-6 text-center">
            <Link
              to="/team/coaching-staff"
              className="inline-flex items-center bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 font-georgian"
            >
              სრული სამწვრთნელო შტაბი
            </Link>
          </div>
        </div>

        {/* Medical Staff */}
        <div className="mt-8 bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-6">
            სამედიცინო პერსონალი
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4">
                ე
              </div>
              <h4 className="font-semibold text-gray-900 font-georgian mb-2">ექიმი</h4>
              <p className="text-sm text-gray-600 font-georgian">დოქტორი გიორგი მედიცინელი</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4">
                ფ
              </div>
              <h4 className="font-semibold text-gray-900 font-georgian mb-2">ფიზიოთერაპევტი</h4>
              <p className="text-sm text-gray-600 font-georgian">მარიამ რეაბილიტაციელი</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Team;
